import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ArrowRight, Menu, MessageCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAppStore } from '../store/useAppStore';
import { Button, Reveal } from '../shared/ui';
import { useScrollToTop } from '../shared/hooks/useScrollToTop';

const ctaCls = '!text-[15px] !font-semibold !px-[18px] !rounded-full !normal-case !tracking-normal';

const navPill = 'rounded-full px-2.5 py-2 text-[15px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent';

// Section links jump within the Home page; About and Contact are their own pages.
const navLinks = [
  { to: '/#services', label: 'What we haul' },
  { to: '/#how', label: 'How it works' },
  { to: '/#coverage', label: 'Coverage' },
  { to: '/about', label: 'About' },
  { to: '/#contact', label: 'Contact' },
];

export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const { isSignedIn } = useAuth();
  const user = useAppStore((s) => s.user);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  useScrollToTop();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Jump to a #section after navigation (runs after useScrollToTop's reset to the top).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 60);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  // Close the menu on navigation, when the viewport grows to desktop, and lock page scroll while open.
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => { if (mq.matches) setMenuOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const closeMenu = () => {
    setClosing(true);
    setTimeout(() => { setMenuOpen(false); setClosing(false); }, 220);
  };

  const portalHref = user?.role === 'customer' ? '/app/requests' : '/staff-portal';
  const isActive = (to: string) => !to.includes('#') && location.pathname === to;

  return (
    <div className="min-h-screen flex flex-col bg-black text-[#f5f5f7]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-black"
      >
        Skip to main content
      </a>
      {/* Nav */}
      <header className={`sticky top-0 z-40 border-b-2 border-[#1d1d1f] animate-nav-in transition-[background-color,backdrop-filter] duration-500 ${scrolled && !menuOpen ? 'bg-black/70 backdrop-blur-xl backdrop-saturate-150' : 'bg-black'}`}>
        <div className="mx-auto flex min-h-[72px] max-w-[1200px] items-center justify-between gap-6 px-6 py-3">
          <Link to="/" aria-label="RT Equipment & Haulage home">
            <img src="/logo-white.png" alt="RT Equipment & Haulage" className="h-10 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="ml-auto hidden items-center gap-6 lg:flex">
            <nav className="flex items-center gap-0.5">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`${navPill} ${isActive(l.to) ? 'bg-[#1c1c1e] text-white' : 'text-[#d2d2d7] hover:bg-[#1c1c1e] hover:text-white'}`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2.5">
              {isSignedIn ? (
                <Button to={portalHref} variant="marketing-primary" size="md" className={`${ctaCls} !py-2.5`}>
                  Go to portal
                </Button>
              ) : (
                <>
                  <Link to="/login" className="rounded-full border-2 border-[#424245] px-[18px] py-2.5 text-[15px] font-semibold text-[#f5f5f7] outline-none transition-all hover:scale-105 hover:border-[#86868b] focus-visible:ring-2 focus-visible:ring-accent">
                    Sign in
                  </Link>
                  <Button to="/signup" variant="marketing-primary" size="md" className={`${ctaCls} !py-2.5 hover:scale-105 hover:shadow-[0_10px_30px_rgba(245,165,36,0.35)]`}>
                    Request a haul <ArrowRight size={18} strokeWidth={2.5} />
                  </Button>
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
            className="ml-auto grid h-11 w-11 place-items-center rounded-full border-2 border-[#2c2c2e] text-[#f5f5f7] transition-colors hover:border-[#424245] lg:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={2.5} />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="lg:hidden">
          <div
            className={`fixed inset-0 z-40 bg-black/60 ${closing ? 'animate-scrim-out' : 'animate-scrim-in'}`}
            onClick={closeMenu}
          />
          <aside
            id="mobile-menu"
            aria-label="Menu"
            className={`fixed inset-y-0 right-0 z-50 flex w-[min(88vw,380px)] flex-col overflow-auto border-l-2 border-[#1d1d1f] bg-black ${closing ? 'animate-sidebar-right-out' : 'animate-sidebar-right-in'}`}
          >
            <div className="flex min-h-[72px] items-center justify-between px-6">
              <img src="/logo-white.png" alt="RT Equipment & Haulage" className="h-9 w-auto" />
              <button
                type="button"
                onClick={closeMenu}
                className="grid h-11 w-11 place-items-center rounded-full border-2 border-[#2c2c2e] text-[#f5f5f7] transition-colors hover:border-[#424245]"
                aria-label="Close menu"
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>
            <Reveal kind="stagger" immediate className="flex flex-col px-6 pb-7 pt-2">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={closeMenu}
                  className={`border-b-2 border-[#1d1d1f] py-[18px] text-2xl font-semibold tracking-[-0.02em] outline-none focus-visible:text-accent ${isActive(l.to) ? 'text-accent' : 'text-[#f5f5f7]'}`}
                >
                  {l.label}
                </Link>
              ))}
              <div className="mt-7 flex flex-col gap-3">
                {isSignedIn ? (
                  <Link to={portalHref} onClick={closeMenu} className="rounded-full bg-accent p-4 text-center text-[17px] font-semibold text-black">
                    Go to portal
                  </Link>
                ) : (
                  <>
                    <Link to="/signup" onClick={closeMenu} className="rounded-full bg-accent p-4 text-center text-[17px] font-semibold text-black">
                      Request a haul
                    </Link>
                    <Link to="/login" onClick={closeMenu} className="rounded-full border-2 border-[#424245] p-4 text-center text-[17px] font-semibold text-[#f5f5f7]">
                      Sign in
                    </Link>
                  </>
                )}
              </div>
            </Reveal>
          </aside>
        </div>
      )}

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t-2 border-[#1d1d1f] text-xs text-[#86868b]">
        <Reveal className="mx-auto flex max-w-[1024px] flex-wrap justify-between gap-4 px-[22px] pb-8 pt-6">
          <div className="flex flex-col gap-1">
            <span>© {new Date().getFullYear()} RT Equipment &amp; Haulage. Jamaica.</span>
            <span>
              Built by{' '}
              <a
                href="https://hectyk.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#d2d2d7] transition-colors hover:text-accent"
              >
                Hectyk
              </a>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            {[
              { to: '/about', label: 'About' },
              { to: '/#contact', label: 'Contact' },
              { to: '/login', label: 'Sign in' },
              { to: '/terms', label: 'Terms' },
              { to: '/privacy', label: 'Privacy' },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-[#86868b] transition-colors hover:text-white">
                {l.label}
              </Link>
            ))}
            <a
              href="https://wa.me/18764225370"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="grid h-9 w-9 place-items-center rounded-full border-2 border-[#2c2c2e] text-[#d2d2d7] transition-all hover:-translate-y-0.5 hover:border-accent hover:text-accent"
            >
              <MessageCircle size={18} />
            </a>
          </div>
        </Reveal>
      </footer>
    </div>
  );
}
